import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { CommerceToolsApi } from '@grandvisionhq/commercetools';
import { MediaQuery, StickyCallToAction, Price } from '@grandvisionhq/common';
import { Hr } from '@grandvisionhq/elements';
import { useAddToProductViewHistory, useProductDetailSlots, useVto, useCmsData, isRegularProductAction, NonRxProductCTA, RxGlassesCTA, ProductDetailTitleView, ProductDetailDescriptionView, ProductDetailOpticsFeatures, ProductDetailDisclaimer, StickyAside, isRxProductAction, TryOnButton, VirtualMirrorModal, ProductDetailUsps } from '@grandvisionhq/fun-products';
import { useReviewsContext, Actions, ReviewSnippet } from '@grandvisionhq/reviews';
import { useIntl, useFeatureToggles } from '@grandvisionhq/state';
import { updateQueryParam, getLocalizedValue } from '@grandvisionhq/utils';
import { getActiveVariant, getVariants } from '../../helpers.js';
import { pushOfferingSelectionEvent, pushAddToCartEvent } from '../../utils/DataLayer/data-layer.js';
import { mapVirtualMirrorVariants } from '../../utils/map-virtual-mirror-variants.js';
import { selectOffering } from '../utils/offerings.js';
import { useProductDetailContext } from '../Context/ProductDetailContext.js';
import { useBrandLink } from '../utils/use-brand-link.js';
import { getFramesDisplayPrice } from '../utils/get-frames-display-price.js';
import { ProductImageGalleryContainer } from '../../ProductImageGallery/ProductImageGalleryContainer.js';
import { ProductDetailSpecificationContainer } from '../../ProductDetailSpecification/ProductDetailSpecificationContainer.js';
import { ProductReimbursableLogoContainer } from '../Components/ProductReimbursableLogo/ProductReimbursableLogoContainer.js';
import { ProductLabelTag } from '../Components/ProductLabelTag/ProductLabelTag.js';
import { PDPPriceSection } from '../Components/PDPPriceSection/PDPPriceSection.js';
import { VariantPickerContainer } from '../Components/VariantPicker/VariantPickerContainer.js';
import { OfferingsListContainer } from '../Components/OfferingsList/OfferingsListContainer.js';
import SSP from '../../SSP/SSP.js';

const Frames = ({ activeSku , addToCartRef , onCTAClick , product , showStickyButton , variant , children , handleConversionEvent , storesRoute , sspConfig ,  })=>{
    var ref3, ref1;
    const isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    const { currency , getLabel , hasLabel , locale  } = useIntl();
    const reviews = useReviewsContext();
    const { isReimbursable , filteredOfferings , bookAppointmentRouteConfig  } = useProductDetailContext();
    const brandLink = useBrandLink(product);
    const variants = React.useMemo(()=>[
            product.masterVariant,
            ...product.variants
        ]
    , [
        product
    ]);
    useAddToProductViewHistory({
        variant,
        variants
    });
    const openConfiguratorButtonRef = React.useRef(null);
    const { Slots  } = useProductDetailSlots();
    const { isVtoActive , isVtoFeatureEnabled  } = useVto();
    const { isPrescriptionGlassesEnabled , featureConfigs  } = useFeatureToggles();
    const { disclaimer  } = useCmsData();
    const [isConfiguratorOpen, setIsConfiguratorOpen] = React.useState(false);
    const offerings = React.useMemo(()=>{
        if (!product.offerings) {
            return [];
        }
        // filter offerings based on the isReimbursable
        return product.offerings.filter((offering)=>isReimbursable ? offering.isInsuranceReimbursable : !offering.isInsuranceReimbursable
        );
    }, [
        product.offerings,
        isReimbursable
    ]);
    const [selectedOffering1, setSelectedOffering] = React.useState(()=>{
        return selectOffering(offerings, (filteredOfferings === null || filteredOfferings === void 0 ? void 0 : filteredOfferings[0]) || '');
    });
    const [ctaAction, setCtaAction] = React.useState(()=>getCtaAction(variant, selectedOffering1, isPrescriptionGlassesEnabled)
    );
    const activeVariant = getActiveVariant(product, activeSku);
    const { price: variantPrice  } = variant;
    /**
   * SSP price handling
   */ const [sspState, setSspState] = React.useState(()=>({
            selectedGlasses: 'frameAndMonofocal'
        })
    );
    const handleSspChange = React.useCallback((newState)=>{
        setSspState((state)=>swcHelpers.objectSpread({}, state, newState)
        );
    }, []);
    const displayPrice = getFramesDisplayPrice(variantPrice, selectedOffering1 === null || selectedOffering1 === void 0 ? void 0 : selectedOffering1.price, !isPrescriptionGlassesEnabled ? sspState.totalPrice : undefined);
    const isVirtualMirrorEnabled = (featureConfigs === null || featureConfigs === void 0 ? void 0 : featureConfigs.virtualTryOn) === 'VIRTUAL_MIRROR';
    // Make sure the ReviewsDisplay component gets updated with the currently active SKU
    React.useEffect(()=>{
        if (reviews.enabled) {
            reviews.dispatch(Actions.updateReviewDisplay({
                variantId: activeSku
            }));
        }
    }, [
        activeSku,
        reviews,
        reviews.enabled
    ]);
    React.useEffect(()=>{
        if (selectedOffering1) {
            pushOfferingSelectionEvent(selectedOffering1, {
                isDefaultSelection: true
            });
        }
    // this useEffect should be executed only once, so no dependency is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleOfferingSelection = React.useCallback((selectedOffering)=>{
        setSelectedOffering(selectedOffering);
        pushOfferingSelectionEvent(selectedOffering);
        updateQueryParam('offerings', getLocalizedValue(selectedOffering.title, locale));
    }, // changes on any of the dependencies has no effect on this function
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    const handleCtaClick = React.useCallback(()=>{
        onCTAClick();
        // in case of add to cart, we need to push the event to data layer
        if (ctaAction === 'ADD_TO_CART' && selectedOffering1) {
            pushAddToCartEvent(product, activeSku, selectedOffering1, currency);
        }
    }, [
        ctaAction,
        onCTAClick,
        activeSku,
        selectedOffering1,
        product,
        currency
    ]);
    React.useEffect(()=>{
        const activeAction = getCtaAction(variant, selectedOffering1, isPrescriptionGlassesEnabled);
        setCtaAction(activeAction);
    }, [
        activeSku,
        activeVariant,
        isPrescriptionGlassesEnabled,
        selectedOffering1,
        variant
    ]);
    const handleConfiguratorClose = React.useCallback(()=>{
        var ref;
        setIsConfiguratorOpen(false);
        openConfiguratorButtonRef === null || openConfiguratorButtonRef === void 0 ? void 0 : (ref = openConfiguratorButtonRef.current) === null || ref === void 0 ? void 0 : ref.focus();
    }, [
        openConfiguratorButtonRef
    ]);
    const handleConfiguratorOpen = React.useCallback(()=>{
        handleConversionEvent();
        setIsConfiguratorOpen(true);
    }, []);
    const mappedVtoVariants = isVirtualMirrorEnabled ? mapVirtualMirrorVariants(variants, activeSku, locale) : undefined;
    const deliveryIndicatorSkus = (selectedOffering1 === null || selectedOffering1 === void 0 ? void 0 : selectedOffering1.sku) ? [
        activeSku,
        selectedOffering1.sku,
        ...selectedOffering1.bundledProducts
    ] : [
        activeSku
    ];
    const deliveryItems = deliveryIndicatorSkus.map((sku)=>({
            sku,
            quantity: 1
        })
    );
    const callToActionButton = isRegularProductAction(ctaAction) ? /*#__PURE__*/ React.createElement(NonRxProductCTA, {
        storesRoute: storesRoute,
        action: ctaAction,
        handleConversionEvent: handleConversionEvent,
        addToCartConfig: {
            activeSku,
            offeringSku: selectedOffering1 === null || selectedOffering1 === void 0 ? void 0 : selectedOffering1.sku,
            bundleLineItemSkus: selectedOffering1 === null || selectedOffering1 === void 0 ? void 0 : selectedOffering1.bundledProducts,
            onSuccess: handleCtaClick
        }
    }) : /*#__PURE__*/ React.createElement(RxGlassesCTA, {
        bookAppointmentRouteConfig: bookAppointmentRouteConfig,
        action: ctaAction,
        handleConversionEvent: handleConversionEvent,
        buttonRef: openConfiguratorButtonRef,
        onClickWhenOrderable: handleConfiguratorOpen
    });
    var ref2, _useIncludedFrame, _useIncludedLensPack;
    return /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__main product-detail__main--optical"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "desktop-hidden product-detail__header--mobile"
    }, /*#__PURE__*/ React.createElement(ProductDetailTitleView, {
        title: `${variant.brand} ${variant.modelName}`,
        subtitle: variant.brand,
        href: brandLink,
        sku: activeSku
    }), isSmallScreen && /*#__PURE__*/ React.createElement(ReviewSnippet, {
        className: "product-detail__review-snippet",
        productId: product.masterVariant.sku,
        variantId: activeSku
    })), /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__image-gallery"
    }, /*#__PURE__*/ React.createElement(ProductImageGalleryContainer, null)), /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__description"
    }, /*#__PURE__*/ React.createElement(ProductDetailDescriptionView, {
        header: variant.header,
        description: variant.description,
        usps: variant.usps
    }, /*#__PURE__*/ React.createElement(ProductDetailOpticsFeatures, null))), /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__specification"
    }, /*#__PURE__*/ React.createElement(ProductDetailSpecificationContainer, null)), disclaimer && /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__disclaimer"
    }, /*#__PURE__*/ React.createElement(ProductDetailDisclaimer, null)), /*#__PURE__*/ React.createElement("section", {
        className: "product-detail__aside",
        "aria-label": `${variant.brand} ${variant.modelName}`
    }, /*#__PURE__*/ React.createElement(StickyAside, {
        bottomBoundary: ".product-detail",
        isEnabled: !isSmallScreen
    }, /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", {
        className: "mobile-hidden"
    }, /*#__PURE__*/ React.createElement(ProductDetailTitleView, {
        title: `${variant.brand} ${variant.modelName}`,
        subtitle: variant.brand,
        href: brandLink,
        sku: activeSku
    })), /*#__PURE__*/ React.createElement(ProductReimbursableLogoContainer, {
        className: "product-detail__reimbursable-logo"
    }), ((ref3 = variant.tag) === null || ref3 === void 0 ? void 0 : ref3.value) && /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__product-tag"
    }, /*#__PURE__*/ React.createElement(ProductLabelTag, null)), !!variantPrice && /*#__PURE__*/ React.createElement("div", {
        role: "group",
        "aria-label": getLabel('products.screenreader.priceInformation')
    }, !!(selectedOffering1 === null || selectedOffering1 === void 0 ? void 0 : selectedOffering1.bundleContentDescription) && /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__offering-bundle-description"
    }, getLocalizedValue(selectedOffering1.bundleContentDescription, locale)), /*#__PURE__*/ React.createElement(PDPPriceSection, {
        className: "product-detail__specification-prices",
        originalPrice: displayPrice.originalPrice,
        discountedPrice: displayPrice.discountedPrice,
        discountDescription: variant.discountText,
        discountBadge: variant.discounted,
        bestPrice: variant.bestPrice
    }), Slots.AfterPrice ? /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__after-price-slot"
    }, /*#__PURE__*/ React.createElement(Slots.AfterPrice, null)) : null), !isSmallScreen && /*#__PURE__*/ React.createElement(ReviewSnippet, {
        className: "product-detail__review-snippet",
        productId: product.masterVariant.sku,
        variantId: activeSku
    }), hasLabel('products.frames.contents') && /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__specification-contents"
    }, /*#__PURE__*/ React.createElement("span", {
        className: "product-detail__specification-contents__header"
    }, getLabel('products.including')), /*#__PURE__*/ React.createElement("p", null, getLabel('products.frames.contents'))), /*#__PURE__*/ React.createElement(VariantPickerContainer, {
        variantItems: getVariants(variants, getLocalizedValue(product.slug, locale), variant.productType, locale),
        activeVariantSku: variant.sku,
        activeVariantLabel: variant.frameColorLabel,
        enableStockIndicator: isPrescriptionGlassesEnabled,
        channelAvailability: variant.channelAvailability,
        stockCount: (ref2 = (ref1 = variant.availability) === null || ref1 === void 0 ? void 0 : ref1.availableQuantity) !== null && ref2 !== void 0 ? ref2 : 0,
        isAllowBackOrder: !!variant.allowBackOrder
    }), /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__cart-details",
        ref: addToCartRef
    }, offerings && selectedOffering1 && /*#__PURE__*/ React.createElement(OfferingsListContainer, {
        selectedValue: selectedOffering1.sku,
        offerings: offerings,
        onSelectionChange: handleOfferingSelection
    }), Slots.DynamicDeliveryIndicator ? /*#__PURE__*/ React.createElement(Slots.DynamicDeliveryIndicator, {
        deliveryItems: deliveryItems,
        isExcludedFromCheckout: variant.excludedFromCheckout,
        withRxLens: isRxProductAction(ctaAction)
    }) : null, callToActionButton, Slots.AfterCTA ? /*#__PURE__*/ React.createElement(Slots.AfterCTA, null) : null, activeVariant.vtoEnabled && isVtoFeatureEnabled && /*#__PURE__*/ React.createElement(TryOnButton, null), isVirtualMirrorEnabled && isVtoActive && /*#__PURE__*/ React.createElement(VirtualMirrorModal, {
        variants: mappedVtoVariants
    }), isSmallScreen && showStickyButton && !!variantPrice && /*#__PURE__*/ React.createElement(StickyCallToAction, {
        button: callToActionButton,
        price: /*#__PURE__*/ React.createElement(Price, {
            value: displayPrice.discountedPrice ? displayPrice.discountedPrice : displayPrice.originalPrice,
            currency: currency,
            centAmount: true,
            type: displayPrice.discountedPrice ? 'discount' : 'default'
        })
    })), /*#__PURE__*/ React.createElement(Hr, {
        className: "product-detail__usp-divider"
    }), /*#__PURE__*/ React.createElement(ProductDetailUsps, {
        className: "product-detail__usps"
    })))), sspConfig && /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__configuration"
    }, /*#__PURE__*/ React.createElement(SSP, {
        product: product,
        initialGlasses: sspConfig.initialGlassesKey,
        lensExtras: sspConfig.sspExtraProducts,
        lensExtrasResolver: sspConfig.sspResolver,
        onChange: handleSspChange,
        useIncludedFrame: (_useIncludedFrame = sspConfig.useIncludedFrame) !== null && _useIncludedFrame !== void 0 ? _useIncludedFrame : false,
        useIncludedLensPack: (_useIncludedLensPack = sspConfig.useIncludedLensPack) !== null && _useIncludedLensPack !== void 0 ? _useIncludedLensPack : true
    })), children && /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__children"
    }, children), Slots.Configurator && /*#__PURE__*/ React.createElement(Slots.Configurator, {
        id: "glasses",
        onModalClose: handleConfiguratorClose,
        isOpen: isConfiguratorOpen
    }));
};
function getActiveOfferingActionObject(offering, activeSku) {
    if (!offering || !activeSku) {
        return;
    }
    if (Array.isArray(offering.actions)) {
        /**
     * 'offerings' will only be an array once we're using GraphQL to fetch the product data.
     */ return offering === null || offering === void 0 ? void 0 : offering.actions.find((a)=>a.variant === activeSku
        );
    } else {
        const action = offering.actions[activeSku];
        if (!action) {
            return;
        }
        return {
            variant: activeSku,
            action
        };
    }
}
function getCtaAction(variant, offering, isPrescriptionGlassesEnabled) {
    const fallbackAction = variant.channelAvailability === 'ONLINE' && isPrescriptionGlassesEnabled ? 'OPEN_CONFIGURATOR' : 'OPEN_CONFIGURATOR_UNAVAILABLE';
    if (!offering) {
        return fallbackAction;
    }
    if (offering && CommerceToolsApi.isDeprecatedOffering(offering)) {
        return offering.action;
    }
    const activeOffering = getActiveOfferingActionObject(offering, variant.sku);
    if (!activeOffering) {
        return fallbackAction;
    }
    return activeOffering.action;
}
var Frames$1 = Frames;

export { Frames, Frames$1 as default };
//# sourceMappingURL=Frames.js.map
